import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  Paper,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import { Stack } from "@mui/system";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { getDomain } from "tldts";
import { isWebUri } from "valid-url";
import tlds from "tlds";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { setAddNewPageReqText } from "../../../../../../features/pageSpeedPerformanceSlice";

interface PageScanRequestModalProps {
  open: boolean;
  handleClose: () => void;
  postPageSpeed: Function;
  errorAccToPageStatus: any;
  postedPageSpeedContent: any;
  setOpenModal: any;
  setErrorAccToPageStatus: any;
}

function PageScanRequestModal({
  open,
  handleClose,
  postPageSpeed,
  errorAccToPageStatus,
  setOpenModal,
  setErrorAccToPageStatus,
}: PageScanRequestModalProps) {
  const [showAlert, setShowAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [urlError, setUrlError] = useState("");
  const { addNewPageReqText } = useSelector(
    (state: any) => state.pageSpeedPerformance
  );
  const validateUrl = (value: any) => {
    // Allow empty values for optional field
    if (!value) {
      return "";
    }

    // Check if URL starts or ends with whitespace
    if (value !== value.trim()) {
      return "URL cannot start or end with whitespace";
    }

    // Also allow completely empty trimmed values
    if (!value.trim()) {
      return "";
    }

    const trimmedValue = value.trim();

    // Check if URL starts with special characters (excluding http/https protocol)
    if (
      !/^https?:\/\//.test(trimmedValue) &&
      /^[^a-zA-Z0-9]/.test(trimmedValue)
    ) {
      return "URL cannot start with special characters";
    }

    // Check if URL starts with http:// or https://
    if (!/^https?:\/\//.test(trimmedValue)) {
      return "URL must start with http:// or https://";
    }

    // Check for invalid characters
    if (/[\s]/.test(trimmedValue)) {
      return "URL cannot contain spaces";
    }

    // Validate using valid-url library
    const isValid = isWebUri(trimmedValue);
    if (!isValid) {
      return "Please enter a valid URL";
    }

    // Extract domain and validate TLD
    const baseDomain = getDomain(trimmedValue);
    if (!baseDomain) {
      return "URL must have a valid domain";
    }

    // Check if domain has a valid TLD
    const validTLDs = new Set(tlds);
    const hasValidTLD = [...validTLDs].some((tld) =>
      baseDomain.toLowerCase().endsWith(`.${tld}`)
    );

    if (!hasValidTLD) {
      return "URL must have a valid top-level domain";
    }

    // Check for proper domain structure (at least one dot after protocol)
    const domainPart = trimmedValue.replace(/^https?:\/\//, "");
    if (
      !domainPart.includes(".") ||
      domainPart.startsWith(".") ||
      domainPart.endsWith(".")
    ) {
      return "URL must have a valid domain";
    }

    return "";
  };

  const handleRequest = async () => {
    setErrorAccToPageStatus([]);
    setLoading(true);
    let url = addNewPageReqText.trim();

    if (url.includes("?")) {
      const [base, query] = url.split("?");
      url = base.endsWith("/") ? `${base}?${query}` : `${base}/?${query}`;
    } else {
      url = url.endsWith("/") ? url : `${url}/`;
    }

    const data = { url_name: url };

    try {
      const success = await postPageSpeed(data);
      if (success) {
        handleClose();
      }
    } finally {
      setLoading(false);
    }
  };
  const handleAlertClose = () => {
    setShowAlert(false);
    // Don't clear errorAccToPageStatus to keep modal open
  };

  useEffect(() => {
    if (errorAccToPageStatus?.length > 0) {
      setShowAlert(true);

      if (errorAccToPageStatus[0].status === 201) {
        const timer = setTimeout(() => {
          setShowAlert(false);
          setOpenModal(false);
        }, 4000);
        return () => clearTimeout(timer);
      }
    } else {
      setShowAlert(false);
    }
  }, [errorAccToPageStatus, setOpenModal]);

  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    dispatch(setAddNewPageReqText(value));

    // Validate URL on change
    const error = validateUrl(value);
    setUrlError(error);
  };

  const isSubmitDisabled = () => {
    return addNewPageReqText.trim() === "" || urlError !== "";
  };

  const handleCancel = () => {
    setUrlError("");
    dispatch(setAddNewPageReqText(""));
    setErrorAccToPageStatus([]);
    handleClose();
  };

  return (
    <>
      <Dialog
        open={open}
        PaperProps={{ sx: { width: "900px", maxWidth: "900px" } }}
      >
          <Paper sx={{ padding: "16px 24px 20px 24px" }}>
            <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
              Add New Page Scan Request
            </DialogTitle>
            <TextField
              id="outlined-multiline-flexible"
              value={addNewPageReqText}
              onChange={handleTextFieldChange}
              label="URL*" // Set your label here
              placeholder="Paste the URL of the page you would like to scan here" // Set your placeholder here
              multiline
              color="secondary"
              size="small"
              maxRows={4}
              error={!!urlError}
              helperText={urlError}
              sx={{
                width: "852px",
              }}
            />
            <Typography
              sx={{ fontSize: "12px", marginTop: "8px", color: "#00000099" }}
            >
              Mandatory field
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
              New Page Scan Requests are created with a default active state.
            </Typography>
            {showAlert && errorAccToPageStatus?.length > 0 && (
              <Alert
                iconMapping={{
                  success: <CheckCircleOutlineIcon sx={{ color: "#1E4620" }} />,
                }}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={handleAlertClose}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
                sx={{
                  height: "50%",
                  fontWeight: "500",
                  backgroundColor:
                    errorAccToPageStatus[0].status === 201
                      ? "#EDF7ED"
                      : "#FDEDED",
                  color:
                    errorAccToPageStatus[0].status === 201
                      ? "#1E4620"
                      : "#5F2120",
                  display: "flex",
                  alignItems: "center",
                  padding: "0 4px",
                  border:
                    errorAccToPageStatus[0].status === 201
                      ? "1px solid #4CAF50"
                      : "1px solid #D32F2F80",
                  marginBottom: "10px",
                  marginTop: "10px",
                }}
                severity={
                  errorAccToPageStatus[0].status === 201 ? "success" : "warning"
                }
              >
                {errorAccToPageStatus[0]?.parsedText}
              </Alert>
            )}
            <Stack flexDirection={"row"} justifyContent={"end"} marginTop={2}>
              <Button color="secondary" onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
              <Button
                data-tracker="160"
                variant="contained"
                color="secondary"
                onClick={handleRequest}
                disabled={isSubmitDisabled() || loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {loading ? "Requesting..." : "Request Scan"}
              </Button>
            </Stack>
          </Paper>
        </Dialog>
    </>
  );
}

export default PageScanRequestModal;
